import { FormControlLabel, RadioGroup, makeStyles } from '@material-ui/core';
import { parseInt } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo } from 'react';
import { useCookies } from 'react-cookie';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useAuth0 } from '@auth0/auth0-react';
import {
  HotelRateDisplayPreferences,
  SearchModeLabel,
  SearchModes,
  getSearchModesByAgency,
  defaultStateroomFilling,
  HomeSearchInputFormVariants,
  PageLinks,
  placementOptions,
  UserRoles,
} from '../../../constants';
import checkUserRole from '../../../helpers/selectors/checkUserRole';
import {
  setHotelRateDisplayPreference,
  setSelectedSearchMode,
  setTripSearchInputs,
} from '../../../store/slices/tripSearch';
import WhiteRadio from '../../TwRadio/WhiteRadio';
import FlightSearchbar from './Flight';
import CarSearchBar from '../../TravelOption/CarsInformation/CarSearchBar';
import CruiseSearchbar from './Cruise';
import HotelSearchbar from './Hotel';
import CustomInventorySearchBar from './CustomInventory';
import TourSearchBar from './Tour';
import mapToSearchMode from '../../../utils/mapToSearchMode';
import AgencyNotification from '../../AgencyNotification/index';
import useAgencyNotifications from '../../../hooks/useAgencyNotifications';

const useStyles = makeStyles({
  root: {
    '& .MuiTypography-body1': {
      fontFamily: 'var(--fontFamilyBody)',
      fontSize: 'inherit',
      fontWeight: 'inherit',
    },
    '& .Mui-disabled .MuiTypography-body1': { color: 'rgba(255, 255, 255, 0.7)' },
  },
});

const Searchbar = ({ textColorClass, filterBgColor }) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { user: auth0user } = useAuth0();
  const disabledModes = (auth0user && auth0user.disabledModes) || [];
  const { selectedSearchMode, searchInputs } = useSelector((store) => store.tripSearch);
  const disabledSearchModes = useMemo(
    () => disabledModes.map(mapToSearchMode).filter(Boolean),
    [disabledModes],
  );

  const { travelAgency, isLoggedIn } = useSelector((store) => ({
    travelAgency: store.user.travelAgency || {},
    isLoggedIn: store.user.isLoggedIn,
  }));
  const { searchMode, enableFlights } = router.query;
  const isEndCustomer = useSelector(checkUserRole(UserRoles.CUSTOMER));
  // Clients only get the modes allowed by the agency's client settings, the URL param must not bypass them
  const isFlightsUnlockedByUrl = enableFlights === '' && !isEndCustomer;
  const [cookies] = useCookies();
  const classes = useStyles();
  const homeSearchInputFormVariant =
    travelAgency && travelAgency.siteTheme && travelAgency.siteTheme.homeSearchInputFormVariant;
  const doCenterAlignFilters =
    !isLoggedIn && homeSearchInputFormVariant === HomeSearchInputFormVariants.CENTER_ALIGNED;
  const futureSearchModes = (travelAgency && travelAgency.futureSearchModes) || [];
  const hotelRateDisplayPreference =
    (travelAgency && travelAgency.settings && travelAgency.settings.hotelRateDisplayPreference) ||
    undefined;

  const topBannerNotifications = useAgencyNotifications(placementOptions.TOP_BANNER);

  const getNumberOfAdultsByMode = useCallback(
    (param) => {
      if (!travelAgency.settings) return undefined;
      switch (param) {
        case SearchModes.FLIGHT:
          return travelAgency.settings.defaultFlightTabStaterooms;
        case SearchModes.HOTEL:
          return travelAgency.settings.defaultHotelTabStaterooms;
        case SearchModes.CAR:
          return travelAgency.settings.defaultCarTabStaterooms;
        case SearchModes.CRUISE_TRIP:
          return travelAgency.settings.defaultCruiseTabAdults;
        default:
          break;
      }
      return undefined;
    },
    [travelAgency.settings],
  );

  const handleSetDefaultStaterooms = useCallback(
    (selectedMode) => {
      if (travelAgency.settings) {
        const adultNewValue = getNumberOfAdultsByMode(selectedMode);
        if (adultNewValue) {
          const updatedStaterooms = [defaultStateroomFilling];
          updatedStaterooms[0] = {
            ...updatedStaterooms[0],
            adults: parseInt(adultNewValue),
          };

          dispatch(
            setTripSearchInputs({
              staterooms: updatedStaterooms,
            }),
          );
        }
      }
    },
    [dispatch, getNumberOfAdultsByMode, travelAgency.settings],
  );

  const handleChange = (event) => {
    dispatch(setSelectedSearchMode(event.target.value));

    if (selectedSearchMode && +selectedSearchMode >= 0) {
      // Save user stateroom selection on change selected mode
      const stateroomsByMode = {
        ...searchInputs.stateroomsByMode,
        [selectedSearchMode]: searchInputs.staterooms,
      };

      dispatch(
        setTripSearchInputs({
          stateroomsByMode,
        }),
      );
    }

    // Set default staterooms
    handleSetDefaultStaterooms(event.target.value);

    if (
      event.target.value !== SearchModes.CRUISE_TRIP &&
      searchInputs.staterooms &&
      searchInputs.staterooms.length > 1
    ) {
      // Case: when multiple staterooms selected for cruise and user switch to flight/hotel/car tab,
      // make sure there is one stateroom in list
      dispatch(
        setTripSearchInputs({
          staterooms: [searchInputs.staterooms[0]],
        }),
      );
    }

    if (
      searchInputs &&
      searchInputs.stateroomsByMode &&
      searchInputs.stateroomsByMode[event.target.value]
    ) {
      dispatch(
        setTripSearchInputs({
          staterooms: searchInputs.stateroomsByMode[event.target.value],
        }),
      );
    }
  };
  const enabledTransportModes = useMemo(
    () => getSearchModesByAgency(travelAgency && travelAgency.enabledTransportModes),
    [travelAgency],
  );

  const searchModesByAgency = useMemo(() => {
    if (isFlightsUnlockedByUrl && enabledTransportModes.indexOf(SearchModes.FLIGHT) === -1) {
      return [...enabledTransportModes, SearchModes.FLIGHT];
    }
    return enabledTransportModes.filter((mode) => !disabledSearchModes.includes(mode));
  }, [isFlightsUnlockedByUrl, enabledTransportModes, disabledSearchModes]);
  const isDedicatedHotelPage = router.route === PageLinks.DedicatedHotelPage.route;

  const selectedTransportMode = useMemo(() => {
    if (travelAgency.selectedTransportMode) {
      return disabledSearchModes.length &&
        disabledSearchModes.includes(travelAgency.selectedTransportMode.toString())
        ? enabledTransportModes.find((m) => !disabledSearchModes.includes(m))
        : travelAgency.selectedTransportMode.toString();
    }
    return travelAgency.selectedTransportMode;
  }, [disabledSearchModes, enabledTransportModes, travelAgency.selectedTransportMode]);

  // A results page link must not open a mode the agency hides from clients.
  // Kept as a primitive so the effect below only runs when the mode really changes.
  const isUrlSearchModeHiddenForClient =
    isEndCustomer && searchMode !== undefined && !searchModesByAgency.includes(searchMode);
  const searchModeFromUrl = isUrlSearchModeHiddenForClient
    ? selectedTransportMode || searchModesByAgency[0]
    : searchMode;

  useEffect(() => {
    if (searchModeFromUrl !== undefined) {
      dispatch(setSelectedSearchMode(searchModeFromUrl));
    }
  }, [dispatch, searchModeFromUrl]);

  useEffect(() => {
    if (searchMode === undefined && travelAgency.selectedTransportMode) {
      if (!isDedicatedHotelPage) {
        dispatch(setSelectedSearchMode(selectedTransportMode));
      }
      // On page load set default staterooms using travelAgency settings
      if (!searchInputs.hasInitializedStaterooms) {
        dispatch(
          setTripSearchInputs({
            hasInitializedStaterooms: true,
          }),
        );
        handleSetDefaultStaterooms(travelAgency.selectedTransportMode.toString());
      }
    }
  }, [
    dispatch,
    handleSetDefaultStaterooms,
    isDedicatedHotelPage,
    searchMode,
    travelAgency.selectedTransportMode,
    searchInputs.hasInitializedStaterooms,
    selectedTransportMode,
  ]);

  useEffect(() => {
    if (travelAgency.id) {
      if (!cookies.hotelRateDisplayPreference || Number.isNaN(cookies.hotelRateDisplayPreference)) {
        if (hotelRateDisplayPreference) {
          dispatch(setHotelRateDisplayPreference(Number(hotelRateDisplayPreference)));
        } else {
          dispatch(setHotelRateDisplayPreference(HotelRateDisplayPreferences.TotalRate));
        }
      } else if (+cookies.hotelRateDisplayPreference >= 0) {
        dispatch(setHotelRateDisplayPreference(+cookies.hotelRateDisplayPreference));
      }
    } else if (+cookies.hotelRateDisplayPreference >= 0) {
      dispatch(setHotelRateDisplayPreference(+cookies.hotelRateDisplayPreference));
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, [travelAgency.id]);

  return (
    <>
      {(searchModesByAgency || []).length > 1 || futureSearchModes.length > 0 ? (
        <>
          <RadioGroup
            row
            className={clsx(
              textColorClass,
              classes.root,
              travelAgency.siteTheme && travelAgency.siteTheme.searchbarTabsClass,
              { 'justify-center': doCenterAlignFilters },
            )}
            value={selectedSearchMode}
            onChange={handleChange}
          >
            {searchModesByAgency.map((opt) => (
              <FormControlLabel
                value={opt}
                key={opt}
                control={<WhiteRadio />}
                label={
                  opt === SearchModes.CUSTOM && travelAgency.searchbarTab
                    ? travelAgency.searchbarTab.displayName
                    : SearchModeLabel[opt]
                }
              />
            ))}
            {futureSearchModes.map((mode) => (
              <FormControlLabel // No point in passing value, since its disabled.
                key={mode}
                disabled
                control={<WhiteRadio />}
                label={`${+mode === +SearchModes.CUSTOM && travelAgency.searchbarTab && travelAgency.searchbarTab.displayName ? travelAgency.searchbarTab.displayName : SearchModeLabel[mode]} (coming soon)`}
              />
            ))}
          </RadioGroup>
          {topBannerNotifications.length > 0 &&
            topBannerNotifications.map((notification) => (
              <AgencyNotification
                key={notification.id}
                notification={notification}
                placement={placementOptions.TOP_BANNER}
              />
            ))}
        </>
      ) : (
        <></>
      )}

      {(searchModesByAgency || []).includes(SearchModes.CRUISE_TRIP) &&
        selectedSearchMode === SearchModes.CRUISE_TRIP && <CruiseSearchbar />}
      {selectedSearchMode === SearchModes.HOTEL && (
        <HotelSearchbar
          doShowFlightOriginInput={(searchModesByAgency || []).includes(SearchModes.FLIGHT)}
          filterBgColor={filterBgColor}
        />
      )}
      {selectedSearchMode === SearchModes.FLIGHT && (
        <FlightSearchbar filterBgColor={filterBgColor} searchTypeTextColor={textColorClass} />
      )}
      {selectedSearchMode === SearchModes.CAR && (
        <CarSearchBar filterBgColor={filterBgColor} searchTypeTextColor={textColorClass} />
      )}
      {selectedSearchMode === SearchModes.CUSTOM && (
        <CustomInventorySearchBar tabContent={travelAgency && travelAgency.searchbarTab} />
      )}
      {selectedSearchMode === SearchModes.TOUR && <TourSearchBar />}
    </>
  );
};
Searchbar.propTypes = {
  textColorClass: PropTypes.string,
  filterBgColor: PropTypes.string,
};

Searchbar.defaultProps = {
  textColorClass: 'text-tabsText-0',
  filterBgColor: undefined,
};
export default Searchbar;
